/** User preference for whether `/` opens the floor tablet home. */
export type FloorTabletHomePreference = "auto" | "on" | "off";

const FLOOR_TABLET_HOME_STORAGE_KEY = "hub-floor-tablet-home";

/** Max viewport width treated as a floor tablet (includes 8" landscape). */
export const FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX = 1280;

export function readFloorTabletHomePreference(): FloorTabletHomePreference {
  if (typeof window === "undefined") {
    return "auto";
  }
  try {
    const raw = window.localStorage.getItem(FLOOR_TABLET_HOME_STORAGE_KEY);
    if (raw === "on" || raw === "off" || raw === "auto") {
      return raw;
    }
  } catch {
    // localStorage may be unavailable
  }
  return "auto";
}

export function writeFloorTabletHomePreference(value: FloorTabletHomePreference): void {
  try {
    window.localStorage.setItem(FLOOR_TABLET_HOME_STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

/**
 * Touch-capable device with a tablet-sized viewport (not a wide desktop monitor).
 * Prefer capability signals over user-agent sniffing.
 */
export function isFloorTabletDevice(input?: {
  coarsePointer?: boolean;
  hoverNone?: boolean;
  viewportWidth?: number;
}): boolean {
  if (input != null) {
    const touchCapable = input.coarsePointer === true || input.hoverNone === true;
    const viewportWidth = input.viewportWidth ?? Number.POSITIVE_INFINITY;
    return touchCapable && viewportWidth <= FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX;
  }
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const hoverNone = window.matchMedia("(hover: none)").matches;
  const touchCapable = coarsePointer || hoverNone;
  return touchCapable && window.innerWidth <= FLOOR_TABLET_MAX_VIEWPORT_WIDTH_PX;
}

export function parseFloorTabletHomeQuery(search: string): FloorTabletHomePreference | null {
  const value = new URLSearchParams(search).get("floor")?.trim().toLowerCase();
  if (value === "1" || value === "on" || value === "true") {
    return "on";
  }
  if (value === "0" || value === "off" || value === "false") {
    return "off";
  }
  return null;
}
